'use client';
import React from 'react';

const topArticles = [
{
  category: 'PHYSICS',
  title: 'The Standard Model: A Blueprint of All Matter',
  excerpt: 'Twelve fundamental particles and four forces. The Standard Model is humanity\'s most precise description of reality\'s building blocks.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12d89dcef-1764690409446.png",
  alt: 'Particle physics visualization showing the Standard Model particles and their interactions in a collider',
  tag: 'THEORY'
},
{
  category: 'GEOLOGY',
  title: 'How Earth Built Itself: 4.5 Billion Years of Formation',
  excerpt: 'From a swirling disk of gas and dust to a living planet with oceans, continents, and a magnetic shield — Earth\'s inception is extraordinary.',
  image: "https://images.unsplash.com/photo-1614728672820-e88260ce6d0d",
  alt: 'Satellite view of Earth showing continents, oceans, and atmospheric layers from space',
  tag: 'SCIENCE'
},
{
  category: 'CULTURE',
  title: 'The Inception of Religion: Why Humans Seek the Sacred',
  excerpt: 'Every known human culture has developed religious or spiritual practices. What evolutionary pressure drives us toward the transcendent?',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1529e1c47-1781815453535.png",
  alt: 'Ancient temple ruins with stone carvings and sacred symbols representing the origins of human religious practice',
  tag: 'OBSERVATION'
},
{
  category: 'CHEMISTRY',
  title: 'Carbon: The Molecule That Makes You Possible',
  excerpt: 'Carbon\'s unique bonding properties make it the backbone of all known life. But carbon itself was forged in dying stars billions of years ago.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_171f437fd-1768475793748.png",
  alt: 'Molecular model of carbon compounds showing the tetrahedral bonding structure and organic chemistry',
  tag: 'SCIENCE'
},
{
  category: 'MATHEMATICS',
  title: 'Did Humans Invent Math or Discover It?',
  excerpt: 'Mathematics describes the universe with uncanny precision. Is it a human invention, or were we just uncovering something that already existed?',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1130af13a-1768313931040.png",
  alt: 'Abstract mathematical equations and geometric patterns on a dark background representing pure mathematics',
  tag: 'PHILOSOPHY'
},
{
  category: 'EVOLUTION',
  title: 'The First Eye: How Vision Evolved',
  excerpt: 'The eye evolved independently at least 40 times. The inception of vision may have triggered the Cambrian Explosion of complex life.',
  image: "https://images.unsplash.com/photo-1647467531427-a808b1a2f55f",
  alt: 'Close-up macro photograph of a human eye showing iris patterns and the complexity of biological vision',
  tag: 'BIOLOGY'
},
{
  category: 'TECHNOLOGY',
  title: 'The Inception of the Internet: ARPANET to Now',
  excerpt: 'What began as a military communication network in 1969 became the most transformative technology in human history.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bfd16fa3-1781815452726.png",
  alt: 'Network of fiber optic cables and server infrastructure representing the physical backbone of the internet',
  tag: 'HISTORY'
},
{
  category: 'ASTRONOMY',
  title: 'Dark Matter: The Invisible Scaffolding of the Cosmos',
  excerpt: 'It makes up 27% of the universe, yet we\'ve never directly detected it. Dark matter holds galaxies together — but what is it?',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e0b857a7-1773126151968.png",
  alt: 'Deep space image showing galaxy clusters and gravitational lensing effects attributed to dark matter',
  tag: 'MYSTERY'
},
{
  category: 'NEUROSCIENCE',
  title: 'Memory: How the Brain Writes Its Own History',
  excerpt: 'Every memory you have is a reconstruction, not a recording. The inception of memory is a creative act, not a passive one.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4aee453-1773420195750.png",
  alt: 'Brain scan visualization showing neural activity patterns and memory formation in different brain regions',
  tag: 'SCIENCE'
}];


export default function TopArticles() {
  return (
    <section className="bg-[#070a14] py-10 border-b border-[#1e2a3a]">
      <div className="max-w-[1280px] mx-auto px-6 md:px-16">
        <div className="flex items-center justify-between mb-6">
          <h2
            className="text-white font-semibold"
            style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '25.6px', fontWeight: 500 }}>
            
            Top Reads
          </h2>
          <a
            href="#articles"
            className="text-[#e01a2e] text-sm font-semibold hover:text-white transition-colors duration-200 flex items-center gap-1"
            style={{ fontFamily: 'Roboto, sans-serif' }}>
            
            Browse all
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {topArticles.map((article, i) =>
          <TopArticleCard key={i} article={article} />
          )}
        </div>
      </div>
    </section>);

}

function TopArticleCard({ article }: {article: typeof topArticles[0];}) {
  return (
    <figure className="group cursor-pointer border-b border-[#1e2a3a] pb-5 hover:border-[#e01a2e] transition-colors duration-200">
      <div className="flex items-start gap-4">
        <div className="relative flex-shrink-0 w-20 h-20 overflow-hidden rounded-sm">
          <img
            src={article.image}
            alt={article.alt}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
          
        </div>
        <figcaption className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="text-[9px] font-bold tracking-widest text-[#e01a2e] uppercase"
              style={{ fontFamily: 'Roboto, sans-serif' }}>
              
              {article.category}
            </span>
          </div>
          <h3
            className="text-white font-bold text-[13px] leading-snug mb-1 group-hover:text-[#e01a2e] transition-colors duration-200 line-clamp-2"
            style={{ fontFamily: 'Montserrat, sans-serif' }}>
            
            {article.title}
          </h3>
          <p
            className="text-[#4a5568] text-[12px] leading-relaxed line-clamp-2"
            style={{ fontFamily: 'Roboto, sans-serif' }}>
            
            {article.excerpt}
          </p>
        </figcaption>
      </div>
    </figure>);

}